import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_BASE } from '../../constants/general.constants';
import type { StorageGrant, StorageGrantRequest } from '../../interfaces/storage-grant.interface';

/**
 * The data boundary for a project's storage grants (EHQ-427). Every call is
 * project-Admin-gated server-side; instance admins decide in ehq-admin.
 *
 * Server: `server/src/api/projects/storage_grant_handlers.rs`.
 */
@Injectable({ providedIn: 'root' })
export class StorageGrantService {
  readonly #http = inject(HttpClient);

  /** The project's grants and requests, newest first, ended ones included. */
  async list(projectId: number): Promise<StorageGrant[]> {
    return firstValueFrom(this.#http.get<StorageGrant[]>(`${API_BASE}/project/${projectId}/storage-grants`));
  }

  /** Ask an instance admin for access. Refused with 409 if one is already pending or approved. */
  async request(projectId: number, request: StorageGrantRequest): Promise<StorageGrant> {
    return firstValueFrom(
      this.#http.post<StorageGrant>(`${API_BASE}/project/${projectId}/storage-grants`, request)
    );
  }

  /** Withdraw a pending request or give up an approved grant; its connections stop working. */
  async withdraw(projectId: number, storageGrantId: number): Promise<void> {
    await firstValueFrom(
      this.#http.delete<void>(`${API_BASE}/project/${projectId}/storage-grants/${storageGrantId}`)
    );
  }
}
